import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Outlet } from "react-router-dom";

/**
 * Layout route around the in-quiz student screens that keeps the phone's
 * screen on. With a big class the lobby wait is long enough for a phone to
 * auto-lock, and a locked phone drops its socket. Being a layout route, it
 * stays mounted from the lobby through every question, so the session holds
 * one lock and lets it go when the student reaches the final results.
 *
 * Browsers release the lock whenever the page is hidden, so it is asked for
 * again when the page comes back. Safari (WebKit) grants a lock only with a
 * recent tap, or once a tap-granted lock was held earlier in this page, and a
 * student who joins by QR in character mode never taps before the lobby. So a
 * refused lock is asked for again on the next tap anywhere, with a hint
 * prompting that tap until it is granted. Taps that are refused too (Low Power
 * Mode) hide the hint after the second, since tapping cannot help on that phone; where the API
 * is missing nothing shows. Either way `useStudentRejoin` still recovers a
 * dropped socket.
 */
export default function KeepScreenOn() {
  const { t } = useTranslation();
  const [showHint, setShowHint] = useState(false);

  useEffect(() => {
    if (!("wakeLock" in navigator)) return;
    let cancelled = false;
    // Two refused taps, not one: a swipe can end in a `touchend` that Safari
    // does not count as a tap, and must not hide the hint before a real one.
    let refusedTaps = 0;
    // One iPhone tap fires both `touchend` and `click`; while the tap's request
    // is in flight the second event is the same tap, not another one.
    let tapPending = false;
    let sentinel: WakeLockSentinel | null = null;

    function acquire(fromTap = false) {
      if (document.visibilityState !== "visible") return;
      if (fromTap) tapPending = true;
      navigator.wakeLock.request("screen").then(
        (lock) => {
          if (fromTap) tapPending = false;
          // A request that resolves after unmount (StrictMode remounts in
          // dev, or a fast navigation out) would otherwise leak the lock.
          if (cancelled) {
            lock.release().catch(() => {});
            return;
          }
          // Hiding and showing the page while a request is pending can grant
          // two locks; keep only the newest so cleanup releases them all.
          sentinel?.release().catch(() => {});
          sentinel = lock;
          setShowHint(false);
        },
        () => {
          if (fromTap) tapPending = false;
          if (cancelled) return;
          if (fromTap) refusedTaps += 1;
          // A refusal that lands after another request was granted changes
          // nothing: the screen is already kept on.
          if (sentinel && !sentinel.released) return;
          setShowHint(refusedTaps < 2);
        },
      );
    }

    function onVisible() {
      acquire();
    }
    // Capture and passive, never preventing or stopping the tap, so it still
    // reaches the screen's own buttons. `touchend` because iOS sends no
    // `click` to the document for a tap on something that is not clickable.
    function onTap() {
      if (tapPending || (sentinel && !sentinel.released)) return;
      acquire(true);
    }
    const tapOptions = { capture: true, passive: true };

    acquire();
    document.addEventListener("visibilitychange", onVisible);
    document.addEventListener("touchend", onTap, tapOptions);
    document.addEventListener("click", onTap, tapOptions);
    return () => {
      cancelled = true;
      document.removeEventListener("visibilitychange", onVisible);
      document.removeEventListener("touchend", onTap, tapOptions);
      document.removeEventListener("click", onTap, tapOptions);
      sentinel?.release().catch(() => {});
    };
  }, []);

  return (
    <>
      <Outlet />
      {showHint && (
        <p
          role="status"
          className="pointer-events-none fixed inset-x-0 bottom-4 mx-auto w-fit max-w-[calc(100%-2rem)] rounded-full bg-brand-ink2/90 px-4 py-2 text-center text-sm text-white/80"
        >
          {t("play.keep_awake_hint")}
        </p>
      )}
    </>
  );
}
